/**
 * Next Question — rank unresolved clinical factors by expected impact on the differential.
 * Pure async function; callers inject `getEdgesForFactors` (no Firestore in this module).
 *
 * @module ddxNextQuestion
 */

import {
  computeShiftScores,
  blendScores,
  SHIFT_BLEND_WEIGHTS,
} from './ddxShiftScoring.js';

/** @typedef {import('./ddxTypes.js').ShiftEdge} ShiftEdge */

/**
 * @typedef {'avoid_cant_miss' | 'minimize_testing' | 'maximize_efficiency' | 'confirm_leading'} DdxNextQuestionOptimizationTarget
 */

/**
 * @typedef {Object} DdxNextQuestionSessionState
 * @property {string[]} resolvedFactorIds
 * @property {Record<string, string>} conditionToDxMap
 * @property {Set<string>} cantMissConditionIds
 * @property {string[]} candidateFactorIds
 * @property {string} clinicalContextProfileId
 * @property {Record<string, number>} kbScores
 * @property {(factorIds: string[]) => Promise<Record<string, ShiftEdge[]>>} getEdgesForFactors
 * @property {Record<string, string> | undefined} [factorCategories]
 * Map of factorId → category string ('history'|'exam'|'diagnostic'|'patient_context'|'unknown').
 * If provided, candidates will include a category field for UI grouping and sequencing.
 * @property {{ patientContextAvoidCantMissMultiplier?: number } | undefined} [nextQuestionRankAdjustments]
 * When `optimizationTarget` is `avoid_cant_miss`, multiply ranked `informationGain` for
 * `patient_context` factors by this value (e.g. 0.45) so age/sex-style questions surface less often.
 */

/**
 * @typedef {Object} DdxNextQuestionCandidate
 * @property {string} factorId
 * @property {number} informationGain
 * @property {number} cantMissImpact
 * @property {string[]} topAffectedConditionIds
 * @property {string | null} category
 * 'history'|'exam'|'diagnostic'|'patient_context'|null
 */

/**
 * @param {string} clinicalContextProfileId
 * @returns {number}
 */
function shiftWeightForContext(clinicalContextProfileId) {
  const id = String(clinicalContextProfileId || '');
  if (Object.prototype.hasOwnProperty.call(SHIFT_BLEND_WEIGHTS, id)) {
    return /** @type {number} */ (SHIFT_BLEND_WEIGHTS[/** @type {keyof typeof SHIFT_BLEND_WEIGHTS} */ (id)]);
  }
  return 0.3;
}

/**
 * @param {Set<string> | string[] | null | undefined} raw
 * @returns {Set<string>}
 */
function toConditionSet(raw) {
  if (raw instanceof Set) return raw;
  if (Array.isArray(raw)) return new Set(raw.map(String).filter(Boolean));
  return new Set();
}

/**
 * @param {string} cid
 * @param {Record<string, { shiftScore?: number }>} conditionScores
 * @param {Record<string, string>} conditionToDxMap
 * @param {Record<string, number>} kbScores
 * @param {number} shiftWeight
 * @returns {number}
 */
function blendedForCondition(cid, conditionScores, conditionToDxMap, kbScores, shiftWeight) {
  const row = conditionScores?.[cid];
  const shift = row ? Number(row.shiftScore) || 0 : 0;
  const dx = String(conditionToDxMap?.[cid] || '').trim();
  const kb = dx ? Number(kbScores?.[dx]) || 0 : 0;
  return blendScores(kb, shift, shiftWeight);
}

/**
 * @param {Record<string, { shiftScore?: number }>} baselineScores
 * @param {Record<string, { shiftScore?: number }>} simulatedScores
 * @param {Record<string, string>} conditionToDxMap
 * @param {Record<string, number>} kbScores
 * @param {number} shiftWeight
 * @param {Set<string>} cantMissConditionIds
 */
function deltaMetrics(
  baselineScores,
  simulatedScores,
  conditionToDxMap,
  kbScores,
  shiftWeight,
  cantMissConditionIds
) {
  const keys = new Set([
    ...Object.keys(baselineScores || {}),
    ...Object.keys(simulatedScores || {}),
  ]);
  let delta = 0;
  let cantMissImpact = 0;
  /** @type {Record<string, number>} */
  const absByCondition = {};
  for (const c of keys) {
    const b = blendedForCondition(c, baselineScores, conditionToDxMap, kbScores, shiftWeight);
    const s = blendedForCondition(c, simulatedScores, conditionToDxMap, kbScores, shiftWeight);
    const d = Math.abs(s - b);
    delta += d;
    absByCondition[c] = d;
    if (cantMissConditionIds.has(c)) {
      cantMissImpact += d;
    }
  }
  const topAffectedConditionIds = Object.entries(absByCondition)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([cid]) => cid);
  return { delta, cantMissImpact, topAffectedConditionIds };
}

/**
 * Condition with highest blended score under baseline shift state (KB + shift).
 *
 * @param {Record<string, { shiftScore?: number }>} baselineScores
 * @param {Record<string, string>} conditionToDxMap
 * @param {Record<string, number>} kbScores
 * @param {number} shiftWeight
 * @returns {string | null}
 */
/**
 * Prefer **history** (HPI-style) shift factors for the next *verbal* history question.
 * Excludes `diagnostic` (tests/imaging) by default in the first tier; `exam` and
 * `patient_context` are not included in the strict tier.
 *
 * Fallback order: (1) `history` only → (2) any non-`diagnostic` → (3) original list.
 *
 * @param {string[]} candidateFactorIds
 * @param {Record<string, string | undefined> | null | undefined} factorIdToCategory factorId → category
 * @returns {{ ids: string[], mode: 'history_only' | 'exclude_diagnostic_fallback' | 'unfiltered_fallback' }}
 */
export function filterCandidateFactorIdsForHpiNextQuestion(candidateFactorIds, factorIdToCategory) {
  const ids = [...new Set((candidateFactorIds || []).filter(Boolean).map(String))];
  const normCat = (id) => String(factorIdToCategory?.[id] || '').trim().toLowerCase();
  const history = ids.filter((id) => normCat(id) === 'history');
  if (history.length > 0) {
    return { ids: history, mode: 'history_only' };
  }
  const noDiagnostic = ids.filter((id) => normCat(id) !== 'diagnostic');
  if (noDiagnostic.length > 0) {
    return { ids: noDiagnostic, mode: 'exclude_diagnostic_fallback' };
  }
  return { ids, mode: 'unfiltered_fallback' };
}

function leadingConditionId(baselineScores, conditionToDxMap, kbScores, shiftWeight) {
  const fromShifts = Object.keys(baselineScores || {});
  if (fromShifts.length > 0) {
    let best = fromShifts[0];
    let bestVal = blendedForCondition(best, baselineScores, conditionToDxMap, kbScores, shiftWeight);
    for (let i = 1; i < fromShifts.length; i += 1) {
      const k = fromShifts[i];
      const v = blendedForCondition(k, baselineScores, conditionToDxMap, kbScores, shiftWeight);
      if (v > bestVal) {
        bestVal = v;
        best = k;
      }
    }
    return best;
  }
  let bestC = null;
  let bestV = -Infinity;
  for (const [cid, dxRaw] of Object.entries(conditionToDxMap || {})) {
    const dx = String(dxRaw || '').trim();
    const kb = dx ? Number(kbScores?.[dx]) || 0 : 0;
    const v = blendScores(kb, 0, shiftWeight);
    if (v > bestV) {
      bestV = v;
      bestC = cid;
    }
  }
  return bestC;
}

/**
 * Rank candidate factors by how much resolving them would change blended KB+shift scores.
 *
 * @param {DdxNextQuestionSessionState} sessionState
 * @param {DdxNextQuestionOptimizationTarget} optimizationTarget
 * @param {number} [topN]
 * @returns {Promise<DdxNextQuestionCandidate[]>}
 */
export async function computeNextQuestionCandidates(sessionState, optimizationTarget, topN = 10) {
  const resolvedFactorIds = [...new Set((sessionState.resolvedFactorIds || []).filter(Boolean).map(String))];
  const candidateFactorIds = [...new Set((sessionState.candidateFactorIds || []).filter(Boolean).map(String))];
  const conditionToDxMap = sessionState.conditionToDxMap || {};
  const kbScores = sessionState.kbScores || {};
  const cantMissSet = toConditionSet(sessionState.cantMissConditionIds);
  const shiftWeight = shiftWeightForContext(sessionState.clinicalContextProfileId);
  const { getEdgesForFactors } = sessionState;
  if (typeof getEdgesForFactors !== 'function') {
    throw new Error('computeNextQuestionCandidates: sessionState.getEdgesForFactors must be a function');
  }

  const baselineEdges = await getEdgesForFactors(resolvedFactorIds);
  const baselineScores = await computeShiftScores(resolvedFactorIds, (fid) => baselineEdges[fid] || []);

  const target = String(optimizationTarget || 'minimize_testing');
  const leadId =
    target === 'confirm_leading'
      ? leadingConditionId(baselineScores, conditionToDxMap, kbScores, shiftWeight)
      : null;

  /** @type {DdxNextQuestionCandidate[]} */
  const results = [];

  for (const candidateFactorId of candidateFactorIds) {
    const simulatedFactorIds = [...new Set([...resolvedFactorIds, candidateFactorId])];
    // eslint-disable-next-line no-await-in-loop -- correctness over batching per spec
    const simulatedEdges = await getEdgesForFactors(simulatedFactorIds);
    // eslint-disable-next-line no-await-in-loop
    const simulatedScores = await computeShiftScores(simulatedFactorIds, (fid) => simulatedEdges[fid] || []);

    const { delta, cantMissImpact, topAffectedConditionIds } = deltaMetrics(
      baselineScores,
      simulatedScores,
      conditionToDxMap,
      kbScores,
      shiftWeight,
      cantMissSet
    );

    let informationGain = delta;
    if (target === 'avoid_cant_miss') {
      informationGain = cantMissImpact * 0.8 + delta * 0.2;
    } else if (target === 'confirm_leading') {
      if (leadId) {
        const b = blendedForCondition(leadId, baselineScores, conditionToDxMap, kbScores, shiftWeight);
        const s = blendedForCondition(leadId, simulatedScores, conditionToDxMap, kbScores, shiftWeight);
        informationGain = Math.abs(s - b);
      } else {
        informationGain = 0;
      }
    }

    const adj = sessionState.nextQuestionRankAdjustments;
    const pcMult = adj?.patientContextAvoidCantMissMultiplier;
    if (
      target === 'avoid_cant_miss' &&
      typeof pcMult === 'number' &&
      Number.isFinite(pcMult) &&
      pcMult > 0 &&
      pcMult < 1
    ) {
      const cat = sessionState.factorCategories?.[candidateFactorId];
      if (cat === 'patient_context') {
        informationGain *= pcMult;
      }
    }

    results.push({
      factorId: candidateFactorId,
      informationGain,
      cantMissImpact,
      topAffectedConditionIds,
      category: sessionState.factorCategories?.[candidateFactorId] ?? null,
    });
  }

  results.sort((a, b) => b.informationGain - a.informationGain);
  const n = Math.max(0, Number(topN) || 10);
  return results.slice(0, n);
}
